import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { fetchDelegations } from '@/api'
import CopyWithTooltip from '@/common/CopyWithTooltip'
import GradientWrapper from '@/common/GradientWrapper'
import ResponsiveModalDrawer from '@/common/ResponsiveModalDrawer'
import ValidatorSelect from '@/common/ValidatorSelect'
import { bridgelessChainInfo } from '@/config'
import { useKeplrWallet } from '@/contexts/KeplrWalletProvider'
import {
  emitter,
  ErrorHandler,
  formatAddress,
  formatBridgeAtomic,
  parseHumanToAtomic,
} from '@/helpers'
import {
  buildNativeDelegateMsg,
  NativeDelegateError,
} from '@/libs/keplr/messages'
import { queryKeys } from '@/queries/keys'
import UiRing from '@/ui/loaders/UiRing'
import { UiButton } from '@/ui/UiButton'

const delegateSchema = z.object({
  validatorAddress: z.string().min(1, 'Validator is required'),
  amount: z
    .string()
    .min(1, 'Amount is required')
    .refine(value => !isNaN(Number(value)) && Number(value) > 0, {
      message: 'Amount must be greater than 0',
    }),
})

type DelegateFormValues = z.infer<typeof delegateSchema>

interface NativeDelegateModalProps {
  open: boolean
  onClose: () => void
}

export default function NativeDelegateModal({
  open,
  onClose,
}: NativeDelegateModalProps) {
  const { signAndBroadcast, address, balance } = useKeplrWallet()
  const queryClient = useQueryClient()

  const form = useForm<DelegateFormValues>({
    resolver: zodResolver(delegateSchema),
    mode: 'onChange',
    defaultValues: {
      validatorAddress: '',
      amount: '',
    },
  })

  const {
    handleSubmit,
    watch,
    setValue,
    formState: { isSubmitting, errors },
  } = form

  // Fetch delegations to check if already delegated
  const { isLoading: isLoadingDelegation } = useQuery({
    queryKey: queryKeys.staking.delegations(address ?? ''),
    enabled: Boolean(open && address),
    queryFn: async () => fetchDelegations(address!),
    staleTime: 30_000,
    refetchInterval: 30_000,
  })

  const availableBalanceAtomic = balance ?? '0'

  const delegateTx = useMutation({
    mutationKey: queryKeys.tx.nativeDelegate(address ?? ''),
    mutationFn: async (values: DelegateFormValues) => {
      if (!address) {
        throw new Error('Wallet not connected')
      }

      const amountAtomic = parseHumanToAtomic(
        values.amount,
        bridgelessChainInfo.stakeCurrency.coinDecimals,
      )

      if (BigInt(amountAtomic) <= 0n) {
        throw new Error('Amount must be greater than 0')
      }

      if (BigInt(amountAtomic) > BigInt(availableBalanceAtomic)) {
        throw new Error('Insufficient balance')
      }

      const msg = buildNativeDelegateMsg({
        delegatorAddress: address,
        validatorAddress: values.validatorAddress,
        amount: {
          denom: bridgelessChainInfo.stakeCurrency.coinMinimalDenom,
          amount: amountAtomic,
        },
      })

      return signAndBroadcast([msg])
    },
    onSuccess: txHash => {
      emitter.emit('success', {
        title: 'Delegation sent',
        message: `TxHash: ${formatAddress(txHash)}`,
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.staking.delegations(address ?? ''),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.bank.balances(address ?? ''),
      })
      onClose()
      form.reset()
    },
    onError: error => {
      ErrorHandler.process(NativeDelegateError.fromUnknown(error))
    },
  })

  const isLoading = isSubmitting || delegateTx.isPending

  return (
    <ResponsiveModalDrawer
      open={open}
      onOpenChange={v => !v && !isLoading && onClose()}
      title={<span className='text-foreground'>Delegate Native Token</span>}
      content={
        <form
          onSubmit={handleSubmit(values => delegateTx.mutate(values))}
          className='text-foreground mt-4 flex flex-col gap-4 p-1'
        >
          <div className='flex flex-col gap-1'>
            <span className='text-muted-foreground text-xs'>Your Address</span>
            <div className='bg-input flex h-12 items-center gap-2 rounded-lg px-3 py-2'>
              <span className='muted-foreground text-sm'>
                {formatAddress(address ?? '', 20, 15)}
              </span>
              <div className='ml-auto'>
                <CopyWithTooltip value={address ?? ''} />
              </div>
            </div>
          </div>

          <div className='flex flex-col gap-1'>
            <span className='text-muted-foreground text-xs'>Validator</span>
            <ValidatorSelect
              value={watch('validatorAddress')}
              disabled={isLoading}
              triggerClassName='w-full'
              contentClassName='w-full'
              onChange={v =>
                setValue('validatorAddress', v, { shouldValidate: true })
              }
            />
            {errors.validatorAddress && (
              <span className='text-xs text-red-400'>
                {errors.validatorAddress.message as string}
              </span>
            )}
          </div>

          <div className='flex flex-col gap-1'>
            <span className='text-muted-foreground text-xs'>Amount</span>
            <GradientWrapper>
              <div className='relative'>
                <input
                  type='text'
                  placeholder='0.0'
                  className='bg-input text-foreground h-12 w-full rounded-lg px-3 py-2 pr-16 text-sm'
                  {...form.register('amount')}
                  disabled={isLoading}
                />
                <button
                  type='button'
                  className='text-primary hover:text-primary/80 absolute top-1/2 right-3 -translate-y-1/2 text-xs font-medium'
                  onClick={() => {
                    const maxAmount = formatBridgeAtomic(availableBalanceAtomic)
                    setValue('amount', maxAmount, { shouldValidate: true })
                  }}
                  disabled={isLoading || BigInt(availableBalanceAtomic) <= 0n}
                >
                  Max
                </button>
              </div>
            </GradientWrapper>
            {errors.amount && (
              <span className='text-xs text-red-400'>
                {errors.amount.message as string}
              </span>
            )}
            <span className='text-muted-foreground text-xs'>
              Available: {formatBridgeAtomic(availableBalanceAtomic)} BRIDGE
            </span>
          </div>
        </form>
      }
      footer={
        <div className='flex w-full gap-2'>
          <UiButton
            variant='outline2'
            className='h-12 flex-1'
            onClick={onClose}
            disabled={isLoading}
          >
            Close
          </UiButton>

          <UiButton
            variant='outline'
            className='h-12 flex-1'
            disabled={
              isLoading ||
              isLoadingDelegation ||
              BigInt(availableBalanceAtomic) <= 0n
            }
            onClick={handleSubmit(values => delegateTx.mutate(values))}
          >
            {isLoading ? (
              <span className='flex items-center gap-2'>
                <UiRing size='15' stroke='2' speed='1.65' color='white' />
                Delegating…
              </span>
            ) : (
              'Delegate'
            )}
          </UiButton>
        </div>
      }
      modalProps={{ className: 'bg-card text-foreground rounded-lg max-w-md' }}
      drawerProps={{ className: 'bg-card text-foreground rounded-lg' }}
    />
  )
}
